import React, { useState } from "react";
import OptionsMenuIcon from "@/assets/icons/svg/OptionsMenuIcon";
import { IStatus } from "@/Common/api/types/CommissionCycle";
import { getPositionDisabledToApprove, getPositionDisabledToRetract, isEveryProviderCompleted } from "@/Washouts/helpers/Statuses";
import { usePositionsWithChangeRequests } from "@/Washouts/hooks/common/usePositionsWithChangeRequests";
import useAccessPositions from "@/Washouts/hooks/WashoutReview/useAccessPositions";
import { useAllWashoutReviewActions } from "@/Washouts/hooks/WashoutReview/useAllWashoutReviewActions";
import { Box, Fade, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { get } from "lodash";
import PositionsDialog from "../PositionsDialog";
import { and, or, ternaryResolver } from "@/Common/helpers/sonar";
import {
	IBuildApproveAllWashoutsMenuArgs,
	IBuildGenerateAllWashoutsMenuArgs,
	IBuildRetractAllWashoutsMenuArgs,
	IEllipsesMenuProps,
	IGetMenuOptions,
	IGetMenuPropsCommon,
	IWashoutEllipseMenuActionProps,
} from "@/Washouts/types/WashoutEllipsesMenu";
import { TInput } from "@/Washouts/types/PositionsDialog";

export enum EMenuActionKey {
	ApproveAllWashout = "approve-all-washout",
	RetractAllApprovalWashout = "retract-all-approval",
	GenerateAllWashout = "generate-all-washout",
}

const isClosedStatus = (status = "") => ["POSTED", "FINALIZED"].includes(status);

const getMenuOptions = (args: IGetMenuOptions) => {
	const { approveAllWashoutMenuProps, retractAllWashoutsMenuProps, generateAllWashoutsMenuProps, menus, closeParent } = args;

	const { isDisabled, isPendingMutation, mutation } = approveAllWashoutMenuProps;

	const {
		isDisabled: isDisabledRetract,
		isPendingMutation: isPendingRetract,
		mutation: retractAllWashoutsMutation,
	} = retractAllWashoutsMenuProps;

	const {
		isDisabled: isDisabledGenerate,
		isPendingMutation: isPendingGenerate,
		mutation: generateAllWashoutsMutation,
	} = generateAllWashoutsMenuProps;

	buildApproveAllWashoutsMenu({
		...approveAllWashoutMenuProps,
		menus,
		approveAllWashoutsMutation: mutation,
		isPendingApproveAllWashoutMutation: isPendingMutation,
		isDisabledApprove: isDisabled,
		closeParent,
	} as IBuildApproveAllWashoutsMenuArgs);

	buildRetractAllWashoutsMenu({
		menus,
		...retractAllWashoutsMenuProps,
		retractAllWashoutsMutation,
		isPendingRetractAllWashoutMutation: isPendingRetract,
		isDisabledRetract,
		closeParent,
	} as IBuildRetractAllWashoutsMenuArgs);

	buildGenerateAllWashoutsMenu({
		menus,
		...generateAllWashoutsMenuProps,
		generateAllWashoutsMutation,
		isPendingGenerateAllWashoutMutation: isPendingGenerate,
		isDisabledGenerate,
		closeParent,
	} as IBuildGenerateAllWashoutsMenuArgs);

	return menus;
};

const buildApproveAllWashoutsMenu = (args: IBuildApproveAllWashoutsMenuArgs) => {
	const {
		menus,
		currentPayPeriod,
		partnerPayPeriod,
		isDisabledApprove,
		openPositionsSelector,
		setOpenPositionsSelector,
		approveAllWashoutsMutation,
		isPendingApproveAllWashoutMutation,
		closeParent,
	} = args;

	menus[EMenuActionKey.ApproveAllWashout] = {
		...menus[EMenuActionKey.ApproveAllWashout],
		disabled: isDisabledApprove,
		action: () => {
			setOpenPositionsSelector(true);
			closeParent(null);
		},
		dialogComponent: (
			<PositionsDialog
				open={openPositionsSelector}
				setOpenPositionsSelector={setOpenPositionsSelector}
				currentPayPeriod={currentPayPeriod}
				partnerPayPeriod={partnerPayPeriod}
				action={approveAllWashoutsMutation as (input: TInput) => void}
				isPendingMutation={isPendingApproveAllWashoutMutation}
			/>
		),
	};
};

const buildRetractAllWashoutsMenu = (args: IBuildRetractAllWashoutsMenuArgs) => {
	const {
		menus,
		currentPayPeriod,
		partnerPayPeriod,
		isDisabledRetract,
		openPositionsSelector,
		setOpenPositionsSelector,
		retractAllWashoutsMutation,
		isPendingRetractAllWashoutMutation,
		closeParent,
	} = args;

	menus[EMenuActionKey.RetractAllApprovalWashout] = {
		...menus[EMenuActionKey.RetractAllApprovalWashout],
		disabled: isDisabledRetract,
		action: () => {
			setOpenPositionsSelector(true);
			closeParent(null);
		},
		dialogComponent: (
			<PositionsDialog
				open={openPositionsSelector}
				setOpenPositionsSelector={setOpenPositionsSelector}
				currentPayPeriod={currentPayPeriod}
				partnerPayPeriod={partnerPayPeriod}
				action={retractAllWashoutsMutation as (input: TInput) => void}
				isPendingMutation={isPendingRetractAllWashoutMutation}
				layoutDialogProps={{ title: "Retract Approvals", btnConfirmText: "Retract" }}
			/>
		),
	};
};

const buildGenerateAllWashoutsMenu = (args: IBuildGenerateAllWashoutsMenuArgs) => {
	const {
		menus,
		currentPayPeriod,
		partnerPayPeriod,
		isDisabledGenerate,
		openPositionsSelector,
		setOpenPositionsSelector,
		generateAllWashoutsMutation,
		isPendingGenerateAllWashoutMutation,
		closeParent,
	} = args;

	menus[EMenuActionKey.GenerateAllWashout] = {
		...menus[EMenuActionKey.GenerateAllWashout],
		disabled: isDisabledGenerate,
		action: () => {
			setOpenPositionsSelector(true);
			closeParent(null);
		},
		dialogComponent: (
			<PositionsDialog
				open={openPositionsSelector}
				setOpenPositionsSelector={setOpenPositionsSelector}
				currentPayPeriod={currentPayPeriod}
				partnerPayPeriod={partnerPayPeriod}
				action={generateAllWashoutsMutation as (input: TInput) => void}
				isPendingMutation={isPendingGenerateAllWashoutMutation}
				layoutDialogProps={{ title: "Generate Washouts", btnConfirmText: "Generate" }}
			/>
		),
	};
};

const WashoutEllipseMenuActions = (props: IWashoutEllipseMenuActionProps) => {
	const { anchorEl, setAnchorEl, options } = props;
	const showMenu = Boolean(anchorEl);

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Menu
				id="basic-menu"
				anchorEl={anchorEl}
				open={showMenu}
				onClose={handleClose}
				TransitionComponent={Fade}
				sx={{ mb: 10 }}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				MenuListProps={{
					"aria-labelledby": "basic-menu",
				}}
			>
				{Object.entries(options).map(([key, action]) => (
					<MenuItem key={key} onClick={action.action} disabled={action.disabled}>
						<Typography className="!font-sans !text-[14px]" sx={{ color: "#666666" }} component="span">
							{action.label}
						</Typography>
					</MenuItem>
				))}
			</Menu>
			{Object.entries(options).map(
				([key, action]) =>
					action?.dialogComponent && <React.Fragment key={`dialog-${key}`}>{action.dialogComponent}</React.Fragment>
			)}
		</>
	);
};

export const WashoutEllipseMenu = (props: IEllipsesMenuProps) => {
	const { shouldShowMenu = true, disabled = false, input, disabledMenuAction } = props;
	const {
		commissionCycle,
		payPeriod,
		partnerPayPeriod,
		currentUser,
		locationId,
		washoutConditionsByPosition,
		disabledPositions: disabledPositionsInSidebar,
	} = input;
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [openApprovePositionsSelector, setOpenApprovePositionsSelector] = useState(false);
	const [openRetractPositionsSelector, setOpenRetractPositionsSelector] = useState(false);
	const [openGeneratePositionsSelector, setOpenGeneratePositionsSelector] = useState(false);
	const accessPositions = useAccessPositions();

	const payPeriodId = payPeriod?._id || "";
	const partnerPayPeriodId = partnerPayPeriod?._id || "";
	const commissionCycleId = commissionCycle?._id || "";
	const { disabledByChangeRequests = [] } = usePositionsWithChangeRequests({
		payPeriodId,
	});

	const { disabledByChangeRequests: partnerDisabledByChangeRequests = [] } = usePositionsWithChangeRequests({
		payPeriodId: partnerPayPeriodId,
	});

	const {
		approveAllWashoutsMutation,
		retractAllWashoutsMutation,
		generateAllWashoutsMutation,
		isPendingApproveAllWashoutMutation,
		isPendingRetractAllWashoutMutation,
		isPendingGenerateAllWashoutMutation,
	} = useAllWashoutReviewActions({
		commissionCycleId,
		payPeriodId,
		locationId,
		currentUser,
	});

	const positionStatuses = get(payPeriod, "positionStatus", []) as IStatus[];
	const partnerPositionStatuses = get(partnerPayPeriod, "positionStatus", []) as IStatus[];
	const partnerCommissionCycleId = payPeriod?.nextCCId || payPeriod?.previousCCId || "";

	const disabledInSidebar =
		positionStatuses?.filter((position) => disabledPositionsInSidebar?.[position.position]).map((position) => position.position) || [];

	const disabledPositionsToApprove = getPositionDisabledToApprove({
		payPeriod,
		partnerPayPeriod,
		accessPositions,
		disabledByChangeRequests,
		partnerDisabledByChangeRequests,
		disabledInSidebar,
	});

	const disabledPositionsToRetract = getPositionDisabledToRetract({
		payPeriod,
		partnerPayPeriod,
		accessPositions,
		washoutConditionsByPosition,
		disabledInSidebar,
	});

	const areAllPositionsDisabled = and(
		positionStatuses?.length > 0,
		positionStatuses.every((position) => disabledPositionsToApprove.disabledPositions.includes(position.position))
	);

	const areAllPartnerPositionsDisabled = and(
		partnerPositionStatuses?.length > 0,
		partnerPositionStatuses.every((position) => disabledPositionsToApprove.disabledPartnerPositions.includes(position.position))
	);

	const isDisabledApproveByCC = isClosedStatus(commissionCycle?.status);
	const isPeriodCompleted = isEveryProviderCompleted(payPeriod);

	const shouldDisableApproveWashoutMenu = disabledMenuAction.every((item) => item.shouldDisabledApproveWashoutMenu);
	const shouldDisableRetractWashoutMenu = disabledMenuAction.every((item) => item.shouldDisabledRetractWashoutMenu);
	const shouldDisableGenerateWashoutMenu = disabledMenuAction.every((item) => item.shouldDisabledGenerateWashoutMenu);

	const disabledMenuToGenerate = positionStatuses.filter((position) =>
		disabledMenuAction.some((item) => item.shouldDisabledGenerateWashoutMenu && item.position === position.position)
	);

	const disabledMenuToGeneratePartner = partnerPositionStatuses.filter((position) =>
		disabledMenuAction.some((item) => item.shouldDisabledGenerateWashoutMenu && item.position === position.position)
	);

	const disabledPositionsToGenerate = {
		hasItems: !shouldDisableGenerateWashoutMenu,
		disabledPositions: ternaryResolver(
			Boolean(disabledMenuToGenerate.length),
			disabledMenuToGenerate.map((item) => item.position),
			[]
		),
		disabledPartnerPositions: ternaryResolver(
			Boolean(disabledMenuToGeneratePartner.length),
			disabledMenuToGeneratePartner.map((item) => item.position),
			[]
		),
	};

	const isDisabledApprove = or(
		isPendingApproveAllWashoutMutation,
		isDisabledApproveByCC,
		isPeriodCompleted,
		!positionStatuses?.length,
		areAllPositionsDisabled,
		Boolean(partnerPayPeriod) && Boolean(areAllPartnerPositionsDisabled),
		shouldDisableApproveWashoutMenu
	);

	const isDisabledRetract = or(
		isPendingRetractAllWashoutMutation,
		!disabledPositionsToRetract.hasItems,
		isPeriodCompleted,
		shouldDisableRetractWashoutMenu
	);

	const isDisabledGenerate = or(isPendingGenerateAllWashoutMutation, !disabledPositionsToGenerate.hasItems);

	const approveAllWashoutMenuProps: IGetMenuPropsCommon = {
		locationId,
		currentUser,
		currentPayPeriod: {
			commissionCycleId,
			payPeriod,
			disabledPositions: disabledPositionsToApprove.disabledPositions,
		},
		partnerPayPeriod: {
			commissionCycleId: partnerCommissionCycleId,
			payPeriod: partnerPayPeriod,
			disabledPositions: disabledPositionsToApprove.disabledPartnerPositions,
		},

		openPositionsSelector: openApprovePositionsSelector,
		setOpenPositionsSelector: setOpenApprovePositionsSelector,
		mutation: approveAllWashoutsMutation,
		isPendingMutation: isPendingApproveAllWashoutMutation,
		isDisabled: isDisabledApprove,
	};

	const retractAllWashoutsMenuProps: IGetMenuPropsCommon = {
		locationId,
		currentUser,
		currentPayPeriod: {
			commissionCycleId,
			payPeriod,
			disabledPositions: disabledPositionsToRetract.disabledPositions,
		},
		partnerPayPeriod: {
			commissionCycleId: partnerCommissionCycleId,
			payPeriod: partnerPayPeriod,
			disabledPositions: disabledPositionsToRetract.disabledPartnerPositions,
		},
		openPositionsSelector: openRetractPositionsSelector,
		setOpenPositionsSelector: setOpenRetractPositionsSelector,
		mutation: retractAllWashoutsMutation,
		isPendingMutation: isPendingRetractAllWashoutMutation,
		isDisabled: isDisabledRetract,
	};

	const generateAllWashoutsMenuProps: IGetMenuPropsCommon = {
		locationId,
		currentUser,
		isDisabled: isDisabledGenerate,
		openPositionsSelector: openGeneratePositionsSelector,
		setOpenPositionsSelector: setOpenGeneratePositionsSelector,
		mutation: generateAllWashoutsMutation,
		isPendingMutation: isPendingGenerateAllWashoutMutation,
		currentPayPeriod: {
			commissionCycleId,
			payPeriod,
			disabledPositions: disabledPositionsToGenerate.disabledPositions,
		},
		partnerPayPeriod: {
			commissionCycleId: partnerCommissionCycleId,
			payPeriod: partnerPayPeriod,
			disabledPositions: disabledPositionsToGenerate.disabledPartnerPositions,
		},
	};

	const menuOptions = {
		[EMenuActionKey.ApproveAllWashout]: {
			label: "Approve All Washouts",
			disabled: false,
			action: () => {},
			dialogComponent: null,
		},
		[EMenuActionKey.RetractAllApprovalWashout]: {
			label: "Retract All Approval",
			disabled: false,
			action: () => {},
			dialogComponent: null,
		},
		[EMenuActionKey.GenerateAllWashout]: {
			label: "Generate All Washouts",
			disabled: false,
			action: () => {},
			dialogComponent: null,
		},
	};

	const options = getMenuOptions({
		approveAllWashoutMenuProps,
		retractAllWashoutsMenuProps,
		generateAllWashoutsMenuProps,
		menus: menuOptions,
		closeParent: setAnchorEl,
	});

	const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
		if (disabled) {
			return;
		}
		setAnchorEl(event.currentTarget);
	};

	return (
		<Box>
			<IconButton
				onClick={handleOnClick}
				disabled={disabled}
				id="basic-button"
				aria-controls={shouldShowMenu ? "basic-menu" : undefined}
				aria-haspopup="true"
				sx={{ minWidth: 30, maxWidth: 30, minHeight: 30, maxHeight: 30 }}
				aria-expanded={shouldShowMenu ? "true" : undefined}
			>
				<OptionsMenuIcon disabled={disabled} sx={{ fontSize: "30px" }} />
			</IconButton>
			<WashoutEllipseMenuActions anchorEl={anchorEl} setAnchorEl={setAnchorEl} options={options} />
		</Box>
	);
};
